import React, { useContext, useCallback, useMemo } from 'react';
import { useField } from 'react-final-form';
import { FlexColumn, FormModal, InputField, SelectField } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useToaster from '@ubeya/shared/hooks/useToaster';
import { getDirtyFields, required } from '@ubeya/shared/utils/form';
import styled from 'styled-components';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import { CONTRACT_TYPES_WITH_TOIL_ACCRUAL, PTO_TYPES } from '@ubeya/shared/constants';
import usePtoTypes from '@ubeya/shared/hooks/generic/usePtoTypes';

const Container = styled(FlexColumn)`
  gap: 16px;
`;

const ADJUSTABLE_PTO_TYPE_KEYS = [PTO_TYPES.Holiday, PTO_TYPES.TimeOffInLieu];

const AdjustPtoAllowanceBalanceModal = ({ onClose, addPtoBalanceAdjustment, activeContract }) => {
  const { t } = useContext(ConfigContext);
  const { addResponseError, addError } = useToaster();
  const { ptoTypesOptions } = usePtoTypes();

  const supportsToil = useMemo(() => CONTRACT_TYPES_WITH_TOIL_ACCRUAL.includes(activeContract?.accountContract?.type), [
    activeContract?.accountContract?.type
  ]);

  const adjustableTypeOptions = useMemo(
    () => ptoTypesOptions.filter(({ value }) => ADJUSTABLE_PTO_TYPE_KEYS.includes(value)),
    [ptoTypesOptions]
  );

  const onSubmit = useCallback(
    async (values, form) => {
      const { dirtyFields } = form.getState();
      const dirtyValues = getDirtyFields(values, dirtyFields);
      if (!dirtyValues) return;

      const { amount, reason } = dirtyValues;
      const type = values.type || PTO_TYPES.Holiday;
      if (!amount) {
        addError(t('missingRequiredFields'));
        return;
      }

      try {
        await addPtoBalanceAdjustment({
          amount,
          reason,
          type
        });
        trackEvent('Leave management - adjusted PTO balance', { module: 'Leave Management', type });
      } catch (error) {
        addResponseError(error);
      } finally {
        onClose();
      }
    },
    [addPtoBalanceAdjustment, addResponseError, addError, t, onClose]
  );

  const initialValues = useMemo(() => ({ type: PTO_TYPES.Holiday }), []);

  return (
    <FormModal
      onSubmit={onSubmit}
      initialValues={initialValues}
      size="small"
      requiredWarningFooter={t('missingRequiredFields')}
      onClose={onClose}
      title={t('contractTypes_adjustAllowanceBalance')}
      confirmText={t('save')}
      showCancelButton
      keepDirtyOnReinitialize>
      <Container>
        {supportsToil && (
          <SelectField
            name="type"
            title={t('type')}
            options={adjustableTypeOptions}
            validate={required}
            required
            usePortal
            isMulti={false}
          />
        )}
        <AmountField />
        <InputField name="reason" title={t('reason')} />
      </Container>
    </FormModal>
  );
};

const AmountField = () => {
  const { t } = useContext(ConfigContext);
  const { input } = useField('type', { subscription: { value: true } });
  const titleKey =
    input.value === PTO_TYPES.TimeOffInLieu
      ? 'contract_types_AddOrSubtractAllowanceHours'
      : 'contract_types_AddOrSubtractAllowance';

  return <InputField name="amount" title={t(titleKey)} type="number" validate={required} required />;
};

export default AdjustPtoAllowanceBalanceModal;
